import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { Button, Drawer } from '@/ui';
import { usePhaseDurations } from './usePhaseDurations';
import styles from './HowItWorks.module.css';

/**
 * "NASIL CALISIR?" — teklif akisinin kurallari.
 *
 * Neden var: bu bolum uygulamada HIC YOKTU. Ceviri anahtari (`actions.howItWorks`) katalogda
 * duruyordu ama kaynak agacinda tek bir kullanimi bile yoktu; kullanicilar akisi deneme-yanilma
 * ile ogreniyordu. Mobilde bir karsiligi vardi ama harici tarayiciya cikiyordu ve yalniz
 * musteri profilindeydi.
 *
 * SURELER SUNUCUDAN: yapilandirilabilir olduklari icin metne gomulemezler. Uc erisilemezse
 * istemci varsayilanlariyla ciziliyor -- yardim metni icin uygulamayi bloke etmek orantisiz.
 */
export function HowItWorks({ audience }: { audience: 'customer' | 'exchange' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const durations = usePhaseDurations(open);
  const minutes = (seconds: number) => Math.round(seconds / 60);

  // Buro tarafinda iki adimli onay (2026-09-26): "Odeme alindi" (confirm) isi Odenecekler'e
  // tasir, gonderim + "Islemi tamamla" (send) kapatir. Adim metinleri sure ALMIYOR; sureler
  // asagidaki kurallar bolumunde sunucudan geliyor.
  const steps =
    audience === 'customer'
      ? ['create', 'collect', 'choose', 'pay', 'done']
      : ['see', 'bid', 'wait', 'receive', 'confirm', 'send'];

  return (
    <>
      <Button
        tone="ghost"
        size="sm"
        iconLeft={<HelpCircle size={18} aria-hidden="true" />}
        aria-label={t('actions.howItWorks')}
        onClick={() => {
          setOpen(true);
        }}
      >
        <span className={styles.actionLabel}>{t('actions.howItWorks')}</span>
      </Button>

      <Drawer
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={t('actions.howItWorks')}
        footer={
          <Button
            tone="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('common.close')}
          </Button>
        }
      >
        <ol className={styles.steps}>
          {steps.map((step, index) => (
            <li key={step} className={styles.step}>
              <span className={styles.stepIndex} aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <p className={styles.stepTitle}>{t(`help.${audience}.${step}.title`)}</p>
                <p className={styles.stepBody}>{t(`help.${audience}.${step}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>

        <section className={styles.rules}>
          <h3 className={styles.rulesTitle}>{t('help.rules.title')}</h3>
          <ul className={styles.rulesList}>
            <li>{t('help.rules.offerWindow', { minutes: minutes(durations.offerSeconds) })}</li>
            <li>
              {t('help.rules.decideWindow', {
                minutes: minutes(durations.decideSeconds - durations.offerSeconds),
              })}
            </li>
            <li>
              {t('help.rules.paymentWindow', { minutes: minutes(durations.paymentSeconds) })}
            </li>
            <li>{t('help.rules.noOffers')}</li>
            <li>{t('help.rules.cancel')}</li>
            <li>
              {t('help.rules.warning', {
                hours: Math.round(durations.disputeSeconds / 3600),
              })}
            </li>
            <li>{t('help.rules.grace', { minutes: minutes(durations.graceSeconds) })}</li>
          </ul>
        </section>
      </Drawer>
    </>
  );
}
